'use client';

import { createContext, useContext, useRef, type ReactNode } from 'react';
import {
  createDomainStore,
  initDomainStore,
  type DomainState,
  type DomainStore,
} from '@/store/use-domain.store';
import { useStore } from 'zustand';

export type DomainStoreApi = ReturnType<typeof createDomainStore>;

const DomainStoreContext = createContext<DomainStoreApi | undefined>(undefined);

export interface DomainStoreProviderProps {
  children: ReactNode;
  initialConfig?: DomainState;
}

export const DomainStoreProvider = ({
  children,
  initialConfig,
}: DomainStoreProviderProps) => {
  const storeRef = useRef<DomainStoreApi | null>(null);

  if (storeRef.current === null) {
    const initState = initialConfig || initDomainStore();
    storeRef.current = createDomainStore(initState);
  }

  return (
    <DomainStoreContext.Provider value={storeRef.current}>
      {children}
    </DomainStoreContext.Provider>
  );
};

export const useDomainStore = <T,>(selector: (store: DomainStore) => T): T => {
  const domainStoreContext = useContext(DomainStoreContext);

  if (!domainStoreContext) {
    throw new Error(`useDomainStore must be used within DomainStoreProvider`);
  }

  return useStore(domainStoreContext, selector);
};

// Selector functions for convenience
export const selectDomain = (s: DomainStore) => s.config;
export const selectIsTenant = (s: DomainStore) => s.isTenant;
